import { Link } from 'react-router-dom';
import { Sparkles, Users, Heart, Utensils, ArrowRight, Star, Music, Palette, Mic } from 'lucide-react';

const IMAGES = {
  hero: "/images/hero-children.jpeg",
  stage: "/images/event-stage.jpeg",
  kids: "/images/children-hugging.jpeg"
};

const pillars = [
  {
    icon: Sparkles,
    title: "Building Unstoppable Confidence",
    description: "Through talent showcases and competitions—including beauty pageants, dance, singing, art, and poetry—children have the stage to perform in front of thousands. This builds a deep, authentic confidence that 'cannot be bought.'",
    color: "bg-[#FACC15]",
    textColor: "text-[#422006]",
    activities: [
      { icon: Star, name: "Beauty Pageants" },
      { icon: Music, name: "Dance & Singing" },
      { icon: Palette, name: "Art Exhibitions" },
      { icon: Mic, name: "Poetry & Performance" }
    ]
  },
  {
    icon: Users,
    title: "Mentorship & Growth",
    description: "We provide safe spaces for essential conversations. Our program includes dedicated sessions for girls regarding menstrual health and womanhood, and guidance for boys on their journey toward becoming responsible, empathetic men.",
    color: "bg-[#38BDF8]",
    textColor: "text-white",
    features: ["Health Education", "Life Skills", "Emotional Intelligence", "Leadership Development"]
  },
  {
    icon: Heart,
    title: "Joy & Community",
    description: "Life should be celebrated. We provide games, activities, and a festive environment where children can simply be children, surrounded by a community that loves and supports them.",
    color: "bg-[#FF7A59]",
    textColor: "text-white",
    features: ["Games & Activities", "Community Building", "Celebrations", "Safe Spaces"]
  },
  {
    icon: Utensils,
    title: "Nourishment",
    description: "No child can dream on an empty stomach. Every Fun in the Sun event ensures that all children are provided with nutritious food, because proper nutrition is the foundation for growth and learning.",
    color: "bg-[#22C55E]",
    textColor: "text-white",
    features: ["Nutritious Meals", "Healthy Snacks", "Clean Water", "Food Education"]
  }
];

const EventsPage = () => {
  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative py-24 md:py-32" data-testid="events-hero">
        <div className="absolute inset-0 z-0">
          <img 
            src={IMAGES.stage} 
            alt="Fun in the Sun Stage" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#FDFBF7]/95 via-[#FDFBF7]/80 to-transparent"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl">
            <span className="inline-block px-4 py-2 bg-[#FACC15] text-[#422006] font-bold rounded-full mb-6">
              Our Flagship Program
            </span>
            <h1 className="text-5xl md:text-6xl font-bold text-[#1E293B] font-['Fredoka'] mb-6">
              Fun in the Sun
            </h1>
            <p className="text-2xl font-accent text-[#FF7A59] mb-4">
              Inspiring the Next Generation
            </p>
            <p className="text-xl text-[#64748B] leading-relaxed mb-8">
              At Protopia, we believe that a child's circumstances should never define their potential. 
              Fun in the Sun is designed to bridge the gap for children and teenagers who feel 
              disconnected from the world due to financial or social barriers.
            </p>
            <div className="bg-white/80 backdrop-blur rounded-2xl p-6 border-l-4 border-[#FACC15]">
              <p className="text-lg text-[#1E293B] font-semibold">
                Our goal: To provide the inspiration, motivation, and resources they need to see 
                themselves for who they truly are: <span className="text-[#FACC15]">limitless</span>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Pillars */}
      <section className="py-24 bg-white" data-testid="core-pillars">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="font-accent text-2xl text-[#FF7A59]">Our Approach</span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#1E293B] font-['Fredoka'] mt-2 mb-4">
              Our Core Pillars
            </h2>
            <p className="text-lg text-[#64748B] max-w-3xl mx-auto">
              We achieve our mission through a holistic "all-in-one" event approach that feeds the body, mind, and soul.
            </p>
          </div>
          
          <div className="space-y-12">
            {pillars.map((pillar, index) => (
              <div 
                key={index}
                data-testid={`pillar-detail-${index}`}
                className={`rounded-3xl overflow-hidden shadow-lg ${index % 2 === 0 ? '' : 'md:flex-row-reverse'}`}
              >
                <div className="grid md:grid-cols-2">
                  <div className={`${pillar.color} p-10 md:p-12 flex flex-col justify-center ${index % 2 !== 0 ? 'md:order-2' : ''}`}>
                    <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-6">
                      <pillar.icon className={`w-8 h-8 ${pillar.textColor}`} />
                    </div>
                    <h3 className={`text-3xl font-bold font-['Fredoka'] mb-4 ${pillar.textColor}`}>
                      {pillar.title}
                    </h3>
                    <p className={`text-lg leading-relaxed ${pillar.textColor === 'text-white' ? 'text-white/90' : 'text-[#422006]/80'}`}>
                      {pillar.description}
                    </p>
                  </div>
                  
                  <div className={`bg-[#FDFBF7] p-10 md:p-12 ${index % 2 !== 0 ? 'md:order-1' : ''}`}>
                    {pillar.activities ? (
                      <div>
                        <h4 className="text-xl font-bold text-[#1E293B] mb-6">Activities Include:</h4>
                        <div className="grid grid-cols-2 gap-4">
                          {pillar.activities.map((activity, i) => (
                            <div key={i} className="flex items-center gap-3 bg-white rounded-xl p-4 shadow-sm">
                              <div className="w-10 h-10 bg-[#FACC15]/10 rounded-lg flex items-center justify-center">
                                <activity.icon className="w-5 h-5 text-[#FACC15]" />
                              </div>
                              <span className="font-semibold text-[#1E293B]">{activity.name}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div>
                        <h4 className="text-xl font-bold text-[#1E293B] mb-6">What We Provide:</h4>
                        <div className="space-y-4">
                          {pillar.features.map((feature, i) => (
                            <div key={i} className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-[#22C55E]/10 rounded-full flex items-center justify-center">
                                <svg className="w-4 h-4 text-[#22C55E]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              </div>
                              <span className="font-semibold text-[#1E293B]">{feature}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Purpose */}
      <section className="py-24 bg-[#1E293B]" data-testid="purpose-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="font-accent text-2xl text-[#FACC15]">The Purpose</span>
              <h2 className="text-4xl md:text-5xl font-bold text-white font-['Fredoka'] mt-2 mb-6">
                Creating a Movement
              </h2>
              <p className="text-xl text-white/80 leading-relaxed mb-6">
                We aren't just hosting an event; we are creating a movement across Kenya and the 
                entire African continent.
              </p>
              <p className="text-lg text-white/70 leading-relaxed mb-8">
                We provide the rewards, the stage, and the mentorship—the children provide the 
                talent and the future. Together, we're building something bigger than any single event.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/donate"
                  data-testid="events-donate-btn"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-[#FACC15] text-[#422006] font-bold px-8 py-4 hover:-translate-y-1 transition-all duration-200"
                >
                  Support This Movement
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/volunteer"
                  data-testid="events-volunteer-btn"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-white/10 text-white font-bold px-8 py-4 hover:bg-white/20 transition-all duration-200"
                >
                  Join as Volunteer
                </Link>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <img 
                src={IMAGES.hero} 
                alt="Children at event" 
                className="rounded-2xl w-full h-48 object-cover"
              />
              <img 
                src={IMAGES.kids} 
                alt="Children hugging" 
                className="rounded-2xl w-full h-48 object-cover mt-8"
              />
              <img 
                src={IMAGES.stage} 
                alt="Event stage" 
                className="rounded-2xl w-full h-48 object-cover col-span-2"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EventsPage;
